import { useQuery, useZero } from "@rocicorp/zero/react";
import { useState } from "react";
import { connectorsByIdentifier } from "../../../shared/connectors";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { useCurrentUserGrants } from "../../hooks/useCurrentUserGrants";
import { useMyConnectorGrants } from "../../hooks/useMyConnectorGrants";
import { grantStatus } from "../../lib/connectorStatus";
import { getWritableScopeIds } from "../../lib/getWritableScopeIds";
import { routes, useMakeUrl } from "../../lib/urls";
import { ConfirmModal } from "../ConfirmModal";
import { ConnectorLogo } from "../ConnectorLogo";
import { EmptyState } from "../EmptyState";
import { HelpTooltip } from "../HelpTooltip";
import { Icon } from "../Icon";
import { PopoverManageRow } from "../PopoverManageRow";
import {
  ManageDropdownOpener,
  Row,
  SearchableDropdown,
  SectionLabel,
} from "../SearchableDropdown";
import { Skeleton } from "../Skeleton";
import { Text } from "../Text";
import { StatusDot } from "../StatusDot";
import { StatusText } from "../StatusText";
import { Tooltip } from "../Tooltip";

type SharedAccount = {
  id: string;
  connector: { id: string; name: string };
};

export function ConnectedAccountsButton() {
  const [disconnecting, setDisconnecting] = useState<SharedAccount | null>(
    null
  );
  return (
    <>
      <SearchableDropdown
        aria-label="Accounts you’ve connected"
        searchPlaceholder="Search your accounts…"
        opener={
          <Tooltip content="Accounts you’ve connected">
            <ManageDropdownOpener>
              <Icon name="id-card-lanyard" size={16} />
            </ManageDropdownOpener>
          </Tooltip>
        }
      >
        {(query, close) => (
          <ConnectedAccountsMenu
            query={query}
            close={close}
            onDisconnectShared={setDisconnecting}
          />
        )}
      </SearchableDropdown>
      {disconnecting && (
        <DisconnectSharedAccountModal
          account={disconnecting}
          onClose={() => setDisconnecting(null)}
        />
      )}
    </>
  );
}

function ConnectedAccountsMenu({
  query,
  close,
  onDisconnectShared,
}: {
  query: string;
  close: () => void;
  onDisconnectShared: (account: SharedAccount) => void;
}) {
  const makeUrl = useMakeUrl();
  const z = useZero();
  const [grants, grantsResult] = useMyConnectorGrants();
  const [sharedAccounts, sharedResult] = useQuery(
    queries.mySharedConnectorAccounts()
  );
  const [myGrants] = useCurrentUserGrants();
  const writableIds = getWritableScopeIds(
    myGrants,
    sharedAccounts.flatMap((account) =>
      account.connectedApps.map((connector) => connector.id)
    ),
    "connected_app:write"
  );
  const sections = [
    {
      label: "For your use",
      description:
        "Accounts you connected for apps and workflows to act on your behalf. Other people connect their own accounts. Disconnecting one only stops your access through that account.",
      result: grantsResult,
      accounts: grants.map((grant) => ({
        ...accountDetails(grant.provider, grant.grantedConnector, makeUrl),
        id: grant.id,
        status: grant.status,
        via: grant.subjectKind === "email" ? grant.subjectId : null,
        menuHidden: false,
        onDisconnect: () =>
          z.mutate(
            sharedMutators.revokeOwnConnectorGrant({
              oauthConnectionId: grant.id,
            })
          ),
      })),
    },
    {
      label: "For shared use",
      description:
        "Accounts you connected for connectors to use. Other people with access to those connectors can use the same accounts through apps, workflows, and chats. Disconnecting one stops that access for everyone.",
      result: sharedResult,
      accounts: sharedAccounts.map((account) => {
        const connector = account.connectedApps[0]!;
        const details = accountDetails(account.provider, connector, makeUrl);
        const canDisconnect = writableIds.has(connector.id);
        return {
          ...details,
          id: account.id,
          status: account.status,
          via: null,
          menuHidden: !canDisconnect,
          onDisconnect: () => onDisconnectShared({ id: account.id, connector }),
        };
      }),
    },
  ];

  const needle = query.trim().toLowerCase();
  const filtered = sections.map((section) => ({
    ...section,
    accounts: section.accounts.filter((account) =>
      [account.name, account.providerName, grantStatus(account.status).label]
        .join(" ")
        .toLowerCase()
        .includes(needle)
    ),
  }));
  const hasAccounts = filtered.some((section) => section.accounts.length);
  const loading = sections.some(
    (section) => section.result.type !== "complete"
  );
  return (
    <>
      {filtered.map((section) =>
        section.accounts.length ? (
          <section key={section.label} aria-label={section.label}>
            <SectionLabel style={{ paddingBottom: 6 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <Text variant="text-small-md" color="var(--text-secondary)">
                  {section.label}
                </Text>
                <HelpTooltip content={section.description} />
              </div>
            </SectionLabel>
            {section.accounts.map((account) => {
              const status = grantStatus(account.status);
              return (
                <PopoverManageRow
                  key={account.id}
                  icon={
                    <ConnectorLogo
                      template={{
                        name: account.providerName,
                        identifier: account.provider,
                      }}
                      size={16}
                    />
                  }
                  title={account.name}
                  subtitle={
                    <StatusText>
                      <StatusDot color={status.color} size={10} />
                      {status.label}
                      {account.via && ` · via ${account.via}`}
                    </StatusText>
                  }
                  to={account.link}
                  onNavigate={close}
                  menuLabel={`Account actions for ${account.name}`}
                  menuHidden={account.menuHidden}
                  menuItems={[
                    {
                      left: <Icon name="circle-minus" size={16} />,
                      label: "Disconnect account",
                      onClick: () => {
                        close();
                        account.onDisconnect();
                      },
                    },
                  ]}
                />
              );
            })}
          </section>
        ) : null
      )}
      {!hasAccounts &&
        (loading && !needle ? (
          <div role="status" aria-label="Loading accounts…">
            {[0, 1, 2].map((row) => (
              <Row
                key={row}
                aria-hidden="true"
                style={{ height: 48, pointerEvents: "none" }}
              >
                <Skeleton
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: 4,
                    flexShrink: 0,
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    flex: 1,
                  }}
                >
                  <Skeleton style={{ width: "60%" }} />
                  <Skeleton style={{ width: "35%", height: 10 }} />
                </div>
              </Row>
            ))}
          </div>
        ) : (
          <div style={{ height: 216 }}>
            <EmptyState
              iconName={needle ? "search" : "id-card-lanyard"}
              iconSize={24}
              title={needle ? "No matching accounts" : "No connected accounts"}
              description={
                needle
                  ? "Try a different search."
                  : "Connected accounts are your sign-ins to services like Google or Slack. They let apps, workflows, and chats use those services on your behalf."
              }
            />
          </div>
        ))}
    </>
  );
}

function DisconnectSharedAccountModal({
  account,
  onClose,
}: {
  account: SharedAccount;
  onClose: () => void;
}) {
  const z = useZero();
  const [myGrants] = useCurrentUserGrants();
  const writableIds = getWritableScopeIds(
    myGrants,
    [account.connector.id],
    "connected_app:write"
  );
  return (
    <ConfirmModal
      open
      onClose={onClose}
      onConfirm={() => {
        if (writableIds.has(account.connector.id)) {
          z.mutate(
            sharedMutators.resetOAuthConnection({
              oauthConnectionId: account.id,
            })
          );
        }
        onClose();
      }}
      title="Disconnect shared account?"
      description={`Apps, workflows, and chats using “${account.connector.name}” will lose access to this account for everyone, in every space. Reconnect an account to the connector to restore access.`}
      confirmLabel="Disconnect for everyone"
      destructive
    />
  );
}

function accountDetails(
  provider: string,
  connector: { id: string; name: string } | null | undefined,
  makeUrl: ReturnType<typeof useMakeUrl>
) {
  const providerName = connectorsByIdentifier[provider]?.name ?? provider;
  return {
    name: connector?.name ?? providerName,
    provider,
    providerName,
    link: connector
      ? makeUrl(routes.modal.connector, { id: connector.id })
      : undefined,
  };
}
